import { MantineProvider } from "@mantine/core"
import { Notifications } from "@mantine/notifications"
import { Frame } from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import {
	BrowserRouter,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom"
import { App } from "./App.jsx"
import "./assets/app.css"
import { LoadingScreen } from "./components/LoadingScreen.jsx"
import { LoadingToast } from "./components/LoadingToast.jsx"
import { McpAuthorize } from "./components/McpAuthorize.jsx"
import {
	GlobalStateContext,
	GlobalStateProvider,
} from "./components/providers/GlobalStateProvider.jsx"
import { PolarisProvider } from "./components/providers/PolarisProvider"
import { QueryProvider } from "./components/providers/QueryProvider"
import { auth } from "./firebase.js"
import { useFetchStoreNames } from "./hooks/useFetchStoreNames.js"
import {
	useGetUserStores,
	useSetSelectedStore,
} from "./hooks/useFirestoreUserHooks.js"
import { initializePaddle } from "./hooks/usePaddle.js"
import {
	useGetPaddleCustomerAuthToken,
	useGetSubscriptions,
} from "./hooks/usePaddleHooks.js"
import * as Sentry from "@sentry/react"
import Login from "./pages/Login.jsx"
import Subscription from "./pages/Subscription.jsx"
import posthog from "posthog-js"
import { useTranslation } from "react-i18next"
import SaveStore from "./pages/SaveStore.jsx"
import Account from "./pages/Account.jsx"
import {
	PostHogPageView,
	PosthogProvider,
} from "./components/providers/PosthogProvider.jsx"
import Authentication from "./pages/Authentication.jsx"
import ActivateStorePlan from "./pages/ActivateStorePlan.jsx"
import { notifications } from "@mantine/notifications"

// Shell ensures that we have auth, shop and access token before rendering App.jsx
// Which is needed for any fetching of neccessary data either from Firestore or Shopify Admin API
// All of which we had with Shopify embedded version

// Procedures to load all neccessary data
// 1. [Shell.jsx] Load Auth from Firebase

// 2. [Shell.jsx] Use Auth to load the users stores
// with their respective access tokens and selectedStore via Firestore [/api/firestore/getUserStores]
// - Check if the selectedStore has a subscription, if not display page saying they need to subscribe or switch to another store

// 3. [App.jsx] Load in parallel
// - Store settings via Firestore [/api/getStoreSettings]
// - Store locale and currency via GQL

const Shell = () => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const [user, loadingAuth, errorAuth] = useAuthState(auth)
	const { t } = useTranslation()

	const location = useLocation()
	const [searchParams, setSearchParams] = useSearchParams()
	const storeOverrideParam = searchParams.get("storeOverride")
	const trialOfferAvailableParam = searchParams.get("trialOfferAvailable")
	const navigate = useNavigate()

	const getUserStores = useGetUserStores()
	const setSelectedStore = useSetSelectedStore()
	const fetchStoreNames = useFetchStoreNames()
	const [loadingShopAndAccessToken, setLoadingShopAndAccessToken] =
		useState(true)

	const getSubscriptions = useGetSubscriptions()
	const getPaddleCustomerAuthToken = useGetPaddleCustomerAuthToken()
	const [loadingSubscriptions, setLoadingSubscriptions] = useState(true)

	const getShopAndAccessToken = async (user) => {
		// Chrome extension "Import and Edit" will pass in ?importTo=shop.myshopify.com
		const importTo = searchParams.get("importTo")

		setLoadingShopAndAccessToken(true)
		const {
			stores,
			selectedStore,
			hasCompletedOnBoarding,
			staffForStores,
			storeOwners,
			savedStores,
		} = await getUserStores(user.uid)
		let shop = null
		let accessToken = null

		if (importTo && stores[importTo]) {
			shop = importTo
			accessToken = stores[importTo] || null

			setTimeout(() => {
				searchParams.delete("importTo")
				setSearchParams(searchParams)
			}, 500)
		} else if (
			storeOverrideParam &&
			storeOverrideParam !== state.shop &&
			stores[storeOverrideParam]
		) {
			shop = storeOverrideParam
			accessToken = stores[storeOverrideParam] || null
			await setSelectedStore(state.userId, storeOverrideParam)
		} else if (selectedStore && savedStores.includes(selectedStore)) {
			shop = selectedStore || null
		} else if (selectedStore && stores[selectedStore]) {
			shop = selectedStore || null
			accessToken = stores[selectedStore] || null
		} else {
			shop = Object.keys(stores)?.[0] || null
			accessToken = stores[shop] || null

			if (shop && accessToken) {
				setSelectedStore(user.uid, shop)
			}
		}

		dispatch({
			stores,
			shop,
			accessToken,
			userId: user.uid,
			userEmail: user.email,
			hasCompletedOnBoarding,
			staffForStores,
			storeOwners,
			savedStores,
		})

		setLoadingShopAndAccessToken(false)

		fetchStoreNames(stores).then((storeNames) => {
			dispatch({
				storeNames,
			})
		})

		return { shop, stores, savedStores }
	}

	const getSubscription = async (user, shop) => {
		setLoadingSubscriptions(true)
		const {
			paddleCustomerId,
			hasValidKopyBasicSubscription,
			kopyBasicSubscription,
			hasTrialedKopyBasicBefore,
			otherKopyBasicSubscriptions,
			hasValidKopyPlusSubscription,
			kopyPlusSubscription,
			hasTrialedKopyPlusBefore,
			hasValidKopyProtectSubscription,
			kopyProtectSubscription,
			hasTrialedKopyProtectBefore,
			otherKopyProtectSubscriptions,
			pricing,
			discounts,
			hasTrialedFromEmailOfferBefore,
			hasAnyStoreSubscriptions,
			hasAnyUnlinkedStoreSubscriptions,
			subscriptionLookupFailed,
		} = await getSubscriptions(user.uid, shop, user.email)

		if (paddleCustomerId) {
			window?.Paddle?.Update({ pwCustomer: { id: paddleCustomerId } })
		}

		dispatch({
			paddleCustomerId,
			hasValidKopyBasicSubscription,
			kopyBasicSubscription,
			hasTrialedKopyBasicBefore,
			otherKopyBasicSubscriptions,
			hasValidKopyPlusSubscription,
			kopyPlusSubscription,
			hasTrialedKopyPlusBefore,
			hasValidKopyProtectSubscription,
			kopyProtectSubscription,
			hasTrialedKopyProtectBefore,
			otherKopyProtectSubscriptions,
			pricing,
			discounts,
			hasTrialedFromEmailOfferBefore,
			hasNotTrialedFromEmailOfferAndUrlIsFromEmailOffer:
				hasTrialedFromEmailOfferBefore === false &&
				trialOfferAvailableParam === "true",
			hasAnyStoreSubscriptions,
			hasAnyUnlinkedStoreSubscriptions,
			subscriptionLookupFailed,
		})

		setLoadingSubscriptions(false)

		return { paddleCustomerId }
	}

	useEffect(async () => {
		if (user?.uid) {
			posthog?.identify(user.uid, {
				email: user.email || "",
				name: user.displayName || "",
			})
			if (window.Canny) {
				window.Canny("identify", {
					appID: "6658881f189e78ee55ca8990",
					user: {
						email: user.email,
						name: user.displayName || user.email.split("@")[0],
						id: user.uid,
					},
				})
			}

			const { shop, stores, savedStores } =
				await getShopAndAccessToken(user)

			const { paddleCustomerId } = await getSubscription(user, shop)
			await getPaddleCustomerAuthToken({ paddleCustomerId, store: shop })

			if (
				localStorage.getItem("eapp_shop") &&
				localStorage.getItem("eapp_handoff") &&
				shop &&
				shop !== localStorage.getItem("eapp_shop") &&
				!stores[localStorage.getItem("eapp_shop")] &&
				!savedStores.includes(localStorage.getItem("eapp_shop"))
			) {
				navigate("/savestore?migration")
			}
		} else {
			initializePaddle(null)
		}
	}, [user])

	useEffect(() => {
		if (!state.subscriptionLookupFailed || !user?.uid) return

		const retry = setTimeout(() => {
			getSubscription(user, state.shop)
		}, 5000)

		return () => clearTimeout(retry)
	}, [state.subscriptionLookupFailed, user, state.shop])

	useEffect(() => {
		// The Shopify OAuth callback lands here with ?connected=<shop>.
		const connectedShop = searchParams.get("connected")
		if (!connectedShop) return

		notifications.show({
			message: t("authentication.store_connected_toast", {
				shop: connectedShop,
			}),
			color: "green",
			autoClose: 8000,
		})
		posthog?.capture("store_connected_landing", { shop: connectedShop })
		searchParams.delete("connected")
		setSearchParams(searchParams, { replace: true })
	}, [])

	useEffect(() => {
		// Google Tag Manager conversion event capture
		const conversionQueryParamIsPresent = searchParams.has("subbed_trial")

		if (conversionQueryParamIsPresent) {
			searchParams.delete("subbed_trial")
			setSearchParams(searchParams)
			if (window.dataLayer?.push) {
				window.dataLayer.push({ event: "start_trial" })
			}
			if (window.fbq) {
				window.fbq("track", "StartTrial")
			}
		}

		// Redirect users coming from Copio
		const eapp_shop = searchParams.get("eapp_shop") || null
		const eapp_at = searchParams.get("eapp_at") || null

		if (eapp_shop && eapp_at) {
			searchParams.delete("eapp_shop")
			searchParams.delete("eapp_at")
			setSearchParams(searchParams)

			try {
				localStorage.setItem("eapp_shop", eapp_shop)
				localStorage.setItem("eapp_handoff", "pending")
			} catch (error) {}

			window.__kopyEappHandoffPromise = fetch(
				"/api/firestore/embedded-app-handoff",
				{
					method: "POST",
					credentials: "same-origin",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						shop: eapp_shop,
						accessToken: eapp_at,
					}),
				},
			)
				.then((response) => {
					if (!response.ok) throw new Error("handoff failed")
					try {
						localStorage.setItem("eapp_handoff", "1")
					} catch (error) {}
				})
				.catch(() => {
					try {
						localStorage.removeItem("eapp_handoff")
					} catch (error) {}
				})
		}

		if (document.referrer) {
			localStorage.setItem("referrer", document.referrer)
		}
	}, [])

	const hasKopyBasicOrKopyProtectSubscription =
		state.hasValidKopyBasicSubscription ||
		state.hasValidKopyProtectSubscription
	const isLinkedStore = !!state.shop && !!state.accessToken
	const isSavedStore = state.savedStores.includes(state.shop)

	// MCP OAuth handoff: external clients (e.g. ChatGPT) are redirected here by
	// the server. Show the normal login UI when signed out, then the consent
	// screen — without forcing the user through the store/subscription
	// onboarding flow.
	const isMcpAuthorize = location.pathname === "/mcp-authorize"

	const renderAppropiatePage = () => {
		if (isMcpAuthorize) {
			if (loadingAuth) {
				return <LoadingScreen />
			}
			if (!user) {
				return <Login firebaseAuth={auth} mcpAuthorizeMode={true} />
			}
			return <McpAuthorize />
		}

		if (!loadingAuth && !user) {
			return <Login firebaseAuth={auth} />
		}

		if (
			loadingAuth ||
			loadingShopAndAccessToken ||
			loadingSubscriptions ||
			state.subscriptionLookupFailed
		) {
			return <LoadingScreen />
		}

		if (user && location.pathname === "/savestore") {
			return <SaveStore newUser={!state.hasAnyStoreSubscriptions} />
		}

		if (user && location.pathname === "/account") {
			return <Account />
		}

		if (state.shop && isSavedStore && location.pathname === "/savestore") {
			// User that has saved a store but hasn't created a subscription yet and wants to add another store
			return <SaveStore newUser={false} />
		}

		// Situation A: New user
		if (!state.hasAnyStoreSubscriptions && !state.shop) {
			// Step 1 for new users
			return (
				<Subscription
					newUser={true}
					useNewUserDiscounts={true}
					showOnlyEssentialPlan={true}
				/>
			)
		}
		if (state.hasAnyUnlinkedStoreSubscriptions && !state.shop) {
			// Step 2 for new users
			return <SaveStore newUser={true} />
		}
		if (
			state.hasAnyUnlinkedStoreSubscriptions &&
			state.shop &&
			!isLinkedStore
		) {
			// Step 3 for new users
			return <Authentication newUser={true} />
		}
		if (
			state.hasAnyUnlinkedStoreSubscriptions &&
			state.shop &&
			isLinkedStore &&
			!hasKopyBasicOrKopyProtectSubscription
		) {
			// Store is connected but no subscription is bound to it (the connect
			// ran without an activeSubscriptionId, or the subscription was bought
			// afterwards). Bind one in place; never send the user back through
			// the Shopify install for this.
			return (
				<ActivateStorePlan
					onActivated={() => getSubscription(user, state.shop)}
				/>
			)
		}

		// Situation B: User with other stores linked wants to link new store
		if (
			state.hasAnyStoreSubscriptions &&
			state.shop &&
			isLinkedStore &&
			location.pathname === "/savestore"
		) {
			// Step 1 for existing users
			return <SaveStore newUser={false} />
		}
		if (
			state.hasAnyStoreSubscriptions &&
			state.shop &&
			isSavedStore &&
			!hasKopyBasicOrKopyProtectSubscription
		) {
			// Step 2 for existing users
			return <Subscription newUser={false} />
		}
		if (
			state.hasAnyStoreSubscriptions &&
			state.shop &&
			isSavedStore &&
			hasKopyBasicOrKopyProtectSubscription
		) {
			// Step 3 for existing users
			return <Authentication newUser={false} />
		}

		// Other edge cases
		if (!state.hasAnyStoreSubscriptions && state.shop && isSavedStore) {
			// User that had a store saved but didn't complete the onboarding or user added as staff and accessed /savestore and saved a store
			// No subscriptions at all -> still a new user for pricing: Essential-only + new-user discounts (no Starter)
			return (
				<Subscription
					newUser={false}
					showOnlyEssentialPlan={true}
					useNewUserDiscounts={true}
				/>
			)
		}
		if (
			!hasKopyBasicOrKopyProtectSubscription &&
			state.hasAnyStoreSubscriptions &&
			state.shop &&
			isLinkedStore
		) {
			// User has canceled or expired subscription for the store but has other store subscriptions
			return <Subscription newUser={false} />
		}
		if (
			!hasKopyBasicOrKopyProtectSubscription &&
			!state.hasAnyStoreSubscriptions &&
			state.shop &&
			isLinkedStore
		) {
			// User has canceled or expired subscription for the store and doesn't have any other store subscriptions
			return (
				<Subscription
					newUser={false}
					showOnlyEssentialPlan={true}
					useNewUserDiscounts={true}
				/>
			)
		}
		if (!state.shop) {
			// Cannot render the app if there is no shop
			return <SaveStore newUser={false} />
		}

		return <App />
	}

	return (
		<Frame>
			{renderAppropiatePage()}

			{isMcpAuthorize || (!loadingAuth && !user)
				? ""
				: (loadingAuth ||
						loadingShopAndAccessToken ||
						loadingSubscriptions) && (
						<LoadingToast>
							{loadingAuth
								? t("common.loading_user")
								: loadingShopAndAccessToken
									? t("common.loading_stores")
									: loadingSubscriptions
										? t("common.loading_subscription")
										: null}
						</LoadingToast>
					)}
		</Frame>
	)
}

const withProviders = (AppComponent) => () => (
	<Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
		<PolarisProvider>
			<BrowserRouter>
				<QueryProvider>
					<PosthogProvider>
						<MantineProvider withNormalizeCSS withGlobalStyles>
							<GlobalStateProvider>
								<AppComponent />
								<Notifications
									position="top-right"
									limit={5}
									zIndex={550}
									className="mantine-notifications-container"
								/>
								<PostHogPageView />
							</GlobalStateProvider>
						</MantineProvider>
					</PosthogProvider>
				</QueryProvider>
			</BrowserRouter>
		</PolarisProvider>
	</Sentry.ErrorBoundary>
)

export default withProviders(Shell)
